// GSAP 動畫管理器
import { gsap } from 'gsap'

/**
 * GSAP 動畫管理器
 * 統一管理地圖系統中的所有動畫效果
 */
export class GSAPAnimationManager {
  constructor () {
    this.timeline = gsap.timeline()
    this.activeAnimations = new Map()
  }

  // ==================== 圖標動畫 ====================

  /**
   * 展開圖標動畫
   * @param {Element} element - 圖標元素
   * @param {Object} options - 動畫選項
   */
  expandIcon (element, options = {}) {
    const {
      duration = 0.4,
      delay = 0,
      scale = 1,
      opacity = 1,
      ease = 'power2.out',
      onComplete = null
    } = options

    gsap.killTweensOf(element)

    gsap.set(element, {
      scale: 0,
      opacity: 0
    })

    return gsap.to(element, {
      scale,
      opacity,
      duration,
      delay,
      ease,
      onComplete
    })
  }

  /**
   * 收合圖標動畫
   * @param {Element} element - 圖標元素
   * @param {Object} options - 動畫選項
   */
  collapseIcon (element, options = {}) {
    const {
      duration = 0.3,
      delay = 0,
      scale = 0,
      opacity = 0,
      ease = 'power2.in',
      onComplete = null
    } = options

    gsap.killTweensOf(element)

    return gsap.to(element, {
      scale,
      opacity,
      duration,
      delay,
      ease,
      onComplete
    })
  }

  /**
   * 子圖標波浪展開動畫
   * @param {Array} elements - 子圖標元素陣列
   * @param {Object} options - 動畫選項
   */
  expandChildIcons (elements, options = {}) {
    const {
      duration = 0.4,
      stagger = 0.1,
      scale = 1,
      opacity = 1,
      ease = 'power2.out',
      onComplete = null
    } = options

    elements.forEach(el => gsap.killTweensOf(el))

    elements.forEach(el => {
      gsap.set(el, {
        scale: 0,
        opacity: 0
      })
    })

    return gsap.to(elements, {
      scale,
      opacity,
      duration,
      stagger,
      ease,
      onComplete
    })
  }

  /**
   * 子圖標收合動畫
   * @param {Array} elements - 子圖標元素陣列
   * @param {Object} options - 動畫選項
   */
  collapseChildIcons (elements, options = {}) {
    const {
      duration = 0.3,
      stagger = 0.05,
      scale = 0,
      opacity = 0,
      ease = 'power2.in',
      onComplete = null
    } = options

    elements.forEach(el => gsap.killTweensOf(el))

    return gsap.to(elements, {
      scale,
      opacity,
      duration,
      stagger,
      ease,
      onComplete
    })
  }

  // ==================== 進度環動畫 ====================

  /**
   * 進度環動畫
   * @param {Element} element - 進度環元素
   * @param {Number} percent - 進度百分比 (0-100)
   * @param {Object} options - 動畫選項
   */
  animateProgressRing (element, percent, options = {}) {
    const {
      duration = 0.5,
      ease = 'power2.out',
      onComplete = null
    } = options

    gsap.killTweensOf(element)

    const circumference = 2 * Math.PI * parseFloat(element.getAttribute('r'))
    const strokeDashoffset = circumference * (1 - percent / 100)

    return gsap.to(element, {
      strokeDashoffset,
      duration,
      ease,
      onComplete
    })
  }

  // ==================== 工具提示動畫 ====================

  /**
   * 工具提示淡入動畫
   * @param {Element} element - 工具提示元素
   * @param {Object} options - 動畫選項
   */
  fadeInTooltip (element, options = {}) {
    const {
      duration = 0.3,
      y = 50,
      scale = 0.5,
      opacity = 0.7,
      ease = 'power4.out'
    } = options

    gsap.killTweensOf(element)

    gsap.set(element, {
      y,
      scale,
      opacity
    })

    return gsap.to(element, {
      y: 0,
      scale: 1,
      opacity: 1,
      duration,
      ease
    })
  }

  /**
   * 工具提示淡出動畫
   * @param {Element} element - 工具提示元素
   * @param {Object} options - 動畫選項
   */
  fadeOutTooltip (element, options = {}) {
    const {
      duration = 0.1,
      y = 50,
      scale = 0,
      opacity = 0,
      ease = 'power4.in',
      onComplete = null
    } = options

    gsap.killTweensOf(element)

    return gsap.to(element, {
      y,
      scale,
      opacity,
      duration,
      ease,
      onComplete
    })
  }

  // ==================== 資訊視窗動畫 ====================

  /**
   * 資訊視窗淡入動畫
   * @param {Element} element - 資訊視窗元素
   * @param {Object} options - 動畫選項
   */
  fadeInInfoWindow (element, options = {}) {
    const {
      duration = 0.3,
      y = 50,
      opacity = 0,
      ease = 'power2.out'
    } = options

    gsap.killTweensOf(element)

    gsap.set(element, {
      y,
      opacity
    })

    return gsap.to(element, {
      y: 0,
      opacity: 1,
      duration,
      ease
    })
  }

  /**
   * 資訊視窗淡出動畫
   * @param {Element} element - 資訊視窗元素
   * @param {Object} options - 動畫選項
   */
  fadeOutInfoWindow (element, options = {}) {
    const {
      duration = 0.2,
      y = 50,
      opacity = 0,
      ease = 'power2.in',
      onComplete = null
    } = options

    gsap.killTweensOf(element)

    return gsap.to(element, {
      y,
      opacity,
      duration,
      ease,
      onComplete
    })
  }

  // ==================== 交互動畫 ====================

  /**
   * 圖標懸停動畫
   * @param {Element} element - 圖標元素
   * @param {Object} options - 動畫選項
   */
  hoverIcon (element, options = {}) {
    const {
      duration = 0.2,
      scale = 1.1,
      ease = 'power2.out'
    } = options

    gsap.killTweensOf(element)

    return gsap.to(element, {
      scale,
      duration,
      ease
    })
  }

  /**
   * 圖標懸停結束動畫
   * @param {Element} element - 圖標元素
   * @param {Object} options - 動畫選項
   */
  unhoverIcon (element, options = {}) {
    const {
      duration = 0.2,
      scale = 1,
      ease = 'power2.out'
    } = options

    gsap.killTweensOf(element)

    return gsap.to(element, {
      scale,
      duration,
      ease
    })
  }

  // ==================== 縮放動畫 ====================

  /**
   * 縮放聚焦動畫
   * @param {Element} element - SVG 元素
   * @param {Object} transform - 變換對象
   * @param {Object} options - 動畫選項
   */
  zoomToTransform (element, transform, options = {}) {
    const {
      duration = 0.3,
      ease = 'power2.out'
    } = options

    gsap.killTweensOf(element)

    return gsap.to(element, {
      duration,
      ease,
      onUpdate: () => {
        // 這裡需要根據實際的 D3 zoom 實現來調整
        // 可能需要與 D3 zoom 行為配合
      }
    })
  }

  // ==================== 聚類動畫 ====================

  /**
   * 聚類圓圈展開動畫
   * @param {Element} element - 聚類圓圈元素
   * @param {Object} options - 動畫選項
   */
  expandClusterCircle (element, options = {}) {
    const {
      duration = 0.3,
      radius = 2.5,
      ease = 'power2.out'
    } = options

    gsap.killTweensOf(element)

    gsap.set(element, {
      attr: { r: 0 }
    })

    return gsap.to(element, {
      attr: { r: radius },
      duration,
      ease
    })
  }

  /**
   * 聚類文字淡入動畫
   * @param {Element} element - 聚類文字元素
   * @param {Object} options - 動畫選項
   */
  fadeInClusterText (element, options = {}) {
    const {
      duration = 0.3,
      delay = 0.15,
      opacity = 1,
      ease = 'power2.out'
    } = options

    gsap.killTweensOf(element)

    gsap.set(element, {
      opacity: 0
    })

    return gsap.to(element, {
      opacity,
      duration,
      delay,
      ease
    })
  }

  // ==================== 工具方法 ====================

  /**
   * 停止所有動畫
   */
  killAllAnimations () {
    gsap.killTweensOf('*')
    this.activeAnimations.clear()
  }

  /**
   * 停止特定元素的動畫
   * @param {Element|String} target - 目標元素或選擇器
   */
  killAnimations (target) {
    gsap.killTweensOf(target)
    this.activeAnimations.delete(target)
  }

  /**
   * 創建動畫時間軸
   * @param {Object} options - 時間軸選項
   */
  createTimeline (options = {}) {
    return gsap.timeline(options)
  }

  /**
   * 設置元素初始狀態
   * @param {Element} element - 目標元素
   * @param {Object} properties - 屬性對象
   */
  setInitialState (element, properties) {
    gsap.set(element, properties)
  }
}

// 創建單例實例
export const gsapAnimationManager = new GSAPAnimationManager()

// 導出類別供自定義使用
export default GSAPAnimationManager
